import axios from 'axios';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import {
    genericUploaderUrl,
    sandboxAccountPaymentHistoryUrl,
    sandboxExploreUrl,
    sandboxMyAssignmentsUrl,
    sandboxMyEnrollmentsUrl,
    sandboxTeacherDashboardUrl,
    sandboxTeacherReviewsUrl,
    sandboxUrl,
} from '../../configurations/api/url';
import { SentryCapture } from '../../analytics/Sentry';
import { httpRequest } from '../types';
import { setCache, destroyOneCache } from '../cache/action';
import { CACHE_TYPE, type NotUniqueCacheValue } from '../cache/types';
import { getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import { getFileExtension } from '../../utils/AppUtils';
import { isSandboxClassAssetZip, SANDBOX_CLASS_ASSET_MAX_BYTES } from '../../utils/uploadSandboxClassAsset';
import {
    addSandboxClassAsset,
    addSandboxClassAssetFail,
    addSandboxClassAssetSuccess,
    completeSandboxPurchase,
    completeSandboxPurchaseFail,
    completeSandboxPurchaseSuccess,
    createSandboxCourse,
    createSandboxCourseFail,
    createSandboxCourseSuccess,
    deleteSandboxClassAsset,
    deleteSandboxClassAssetFail,
    deleteSandboxClassAssetSuccess,
    downloadSandboxClassAsset,
    downloadSandboxClassAssetFail,
    downloadSandboxClassAssetSuccess,
    enrollSandboxFree,
    enrollSandboxFreeFail,
    enrollSandboxFreeSuccess,
    fetchLessonProject,
    fetchLessonProjectFail,
    fetchLessonProjectSuccess,
    fetchMySandboxAssignments,
    fetchMySandboxAssignmentsFail,
    fetchMySandboxAssignmentsSuccess,
    fetchMySandboxEnrollments,
    fetchMySandboxEnrollmentsFail,
    fetchMySandboxEnrollmentsSuccess,
    fetchMySandboxLessonAssignments,
    fetchMySandboxLessonAssignmentsFail,
    fetchMySandboxLessonAssignmentsSuccess,
    fetchSandboxClassAssets,
    fetchSandboxClassAssetsFail,
    fetchSandboxClassAssetsSuccess,
    fetchSandboxCourse,
    fetchSandboxCourseFail,
    fetchSandboxCourseSuccess,
    fetchSandboxExplore,
    fetchSandboxExploreFail,
    fetchSandboxExploreSuccess,
    fetchSandboxPaymentHistory,
    fetchSandboxPaymentHistoryFail,
    fetchSandboxPaymentHistorySuccess,
    fetchTeacherDashboard,
    fetchTeacherDashboardFail,
    fetchTeacherDashboardSuccess,
    fetchTeacherPendingReviews,
    fetchTeacherPendingReviewsFail,
    fetchTeacherPendingReviewsSuccess,
    publishSandboxCourse,
    publishSandboxCourseFail,
    publishSandboxCourseSuccess,
    reviewSandboxAssignment,
    reviewSandboxAssignmentFail,
    reviewSandboxAssignmentSuccess,
    saveSandboxProgress,
    saveSandboxProgressFail,
    saveSandboxProgressSuccess,
    submitSandboxAssignment,
    submitSandboxAssignmentFail,
    submitSandboxAssignmentSuccess,
    updateSandboxClassAsset,
    updateSandboxClassAssetFail,
    updateSandboxClassAssetSuccess,
    updateSandboxCourse,
    updateSandboxCourseFail,
    updateSandboxCourseSuccess,
    uploadSandboxClassAssetZip,
    uploadSandboxClassAssetZipFail,
    uploadSandboxClassAssetZipSuccess,
    uploadSandboxCoverImage,
    uploadSandboxCoverImageFail,
    uploadSandboxCoverImageSuccess,
    upsertSandboxCourseRating,
    upsertSandboxCourseRatingFail,
    upsertSandboxCourseRatingSuccess,
} from './actions';
import { settle } from './sagaHelpers';
import type { ISandboxExploreParams } from './types';

function* fetchExploreSaga({
    payload,
    resolve,
    reject,
}: {
    payload?: ISandboxExploreParams;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    const params = payload || {};
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.category) qs.set('category', params.category);
    if (params.featured) qs.set('featured', 'true');
    if (params.min_rating) qs.set('min_rating', String(params.min_rating));
    if (params.sort) qs.set('sort', params.sort);
    if (params.page) qs.set('page', String(params.page));
    const cacheKey = `explore:${qs.toString() || 'all'}`;
    const useCache = yield* isUseCacheEnabled();
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.SANDBOX_EXPLORE, cacheKey);
    if (useCache && cache?.value && !params.q) {
        const cached = cache.value as any;
        yield put(fetchSandboxExploreSuccess(cached));
        settle(resolve, reject, cached);
        return;
    }
    try {
        const url = `${sandboxExploreUrl}${qs.toString() ? `?${qs}` : ''}`;
        const body = yield call(api, url, httpRequest.GET, null, 2);
        const data = {
            items: body?.items || body?.data?.items || [],
            total: body?.total ?? body?.data?.total ?? 0,
            page: body?.page ?? 1,
            limit: body?.limit ?? 20,
        };
        yield put(fetchSandboxExploreSuccess(data));
        yield put(
            setCache({
                key: cacheKey,
                type: CACHE_TYPE.SANDBOX_EXPLORE,
                value: data,
                isUnique: false,
            }),
        );
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchSandboxExploreFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchCourseSaga({
    payload,
    resolve,
    reject,
}: {
    payload: string;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxUrl}/${payload}`, httpRequest.GET, null, 2);
        const data = res.data;
        yield put(fetchSandboxCourseSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchSandboxCourseFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchTeacherDashboardSaga({
    resolve,
    reject,
}: {
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    const cacheKey = 'teacher-dashboard';
    const useCache = yield* isUseCacheEnabled();
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.SANDBOX_TEACHER_DASHBOARD, cacheKey);
    if (useCache && cache?.value) {
        yield put(fetchTeacherDashboardSuccess(cache.value));
        settle(resolve, reject, cache.value);
        return;
    }
    try {
        const res = yield call(api, sandboxTeacherDashboardUrl, httpRequest.GET, null, 2);
        const data = res.data;
        yield put(fetchTeacherDashboardSuccess(data));
        yield put(
            setCache({
                key: cacheKey,
                type: CACHE_TYPE.SANDBOX_TEACHER_DASHBOARD,
                value: data,
                isUnique: false,
            }),
        );
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchTeacherDashboardFail(error));
        settle(resolve, reject, error, true);
    }
}

function* createCourseSaga({
    payload,
    resolve,
    reject,
}: {
    payload: any;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const res = yield call(api, sandboxUrl, httpRequest.POST, payload, 0);
        yield put(createSandboxCourseSuccess(res.data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_TEACHER_DASHBOARD }));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_EXPLORE }));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createSandboxCourseFail(error));
        settle(resolve, reject, error, true);
    }
}

function* updateCourseSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { courseId: string; body: any };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxUrl}/${payload.courseId}`, httpRequest.PATCH, payload.body, 0);
        yield put(updateSandboxCourseSuccess(res.data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_TEACHER_DASHBOARD }));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_EXPLORE }));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateSandboxCourseFail(error));
        settle(resolve, reject, error, true);
    }
}

function* publishCourseSaga({
    payload,
    resolve,
    reject,
}: {
    payload: string;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxUrl}/${payload}/publish`, httpRequest.POST, {}, 0);
        yield put(publishSandboxCourseSuccess(res.data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_TEACHER_DASHBOARD }));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_EXPLORE }));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(publishSandboxCourseFail(error));
        settle(resolve, reject, error, true);
    }
}

function* enrollFreeSaga({
    payload,
    resolve,
    reject,
}: {
    payload: string;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxUrl}/${payload}/enroll-free`, httpRequest.POST, {}, 0);
        yield put(enrollSandboxFreeSuccess(res.data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_ENROLLMENTS }));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(enrollSandboxFreeFail(error));
        settle(resolve, reject, error, true);
    }
}

function* completePurchaseSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { courseId: string; body: any };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxUrl}/${payload.courseId}/complete-purchase`,
            httpRequest.POST,
            payload.body,
            0,
        );
        yield put(completeSandboxPurchaseSuccess(res.data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_ENROLLMENTS }));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(completeSandboxPurchaseFail(error));
        settle(resolve, reject, error, true);
    }
}

function* saveProgressSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { courseId: string; lessonId: string; body: any };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const body: any = {
            position_seconds: Math.max(0, Math.floor(Number(payload.body.position_seconds) || 0)),
        };
        if (payload.body.duration_seconds != null) {
            body.duration_seconds = Math.max(0, Math.floor(Number(payload.body.duration_seconds) || 0));
        }
        if (payload.body.completed) body.completed = true;
        const res = yield call(
            api,
            `${sandboxUrl}/${payload.courseId}/lessons/${payload.lessonId}/progress`,
            httpRequest.PUT,
            body,
            0,
        );
        yield put(saveSandboxProgressSuccess(res.data));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(saveSandboxProgressFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchLessonProjectSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { courseId: string; lessonId: string };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxUrl}/${payload.courseId}/lessons/${payload.lessonId}/project`,
            httpRequest.GET,
            null,
            2,
        );
        const data = res.data;
        const project = data?.project;
        let out = data;
        if (project && typeof project === 'object') {
            const {
                download_link: _d,
                resolutions: _r,
                '1080p': _a,
                '720p': _b,
                '480p': _c,
                '360p': _e,
                ...safe
            } = project as Record<string, unknown>;
            out = { ...data, project: safe };
        }
        yield put(fetchLessonProjectSuccess(out));
        settle(resolve, reject, out);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchLessonProjectFail(error));
        settle(resolve, reject, error, true);
    }
}

function* cachedListFetch(
    cacheType: CACHE_TYPE,
    cacheKey: string,
    url: string,
    success: any,
    fail: any,
    resolve?: (v: any) => void,
    reject?: (e: any) => void,
): Generator<any, void, any> {
    const useCache = yield* isUseCacheEnabled();
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(cacheType, cacheKey);
    if (useCache && cache?.value) {
        yield put(success(cache.value));
        settle(resolve, reject, cache.value);
        return;
    }
    try {
        const res = yield call(api, url, httpRequest.GET, null, 2);
        const data = res?.data ?? res;
        yield put(success(data));
        yield put(
            setCache({
                key: cacheKey,
                type: cacheType,
                value: data,
                isUnique: false,
            }),
        );
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchEnrollmentsSaga({ resolve, reject }: any): Generator<any, void, any> {
    yield* cachedListFetch(
        CACHE_TYPE.SANDBOX_ENROLLMENTS,
        'mine',
        sandboxMyEnrollmentsUrl,
        fetchMySandboxEnrollmentsSuccess,
        fetchMySandboxEnrollmentsFail,
        resolve,
        reject,
    );
}

function* fetchAssignmentsSaga({ resolve, reject }: any): Generator<any, void, any> {
    yield* cachedListFetch(
        CACHE_TYPE.SANDBOX_ASSIGNMENTS,
        'mine',
        sandboxMyAssignmentsUrl,
        fetchMySandboxAssignmentsSuccess,
        fetchMySandboxAssignmentsFail,
        resolve,
        reject,
    );
}

function* fetchTeacherReviewsSaga({ resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(api, sandboxTeacherReviewsUrl, httpRequest.GET, null, 2);
        yield put(fetchTeacherPendingReviewsSuccess(res.data));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchTeacherPendingReviewsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* submitAssignmentSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxUrl}/${payload.courseId}/lessons/${payload.lessonId}/assignments`,
            httpRequest.POST,
            payload.body,
            0,
        );
        yield put(submitSandboxAssignmentSuccess(res.data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_ASSIGNMENTS }));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(submitSandboxAssignmentFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchLessonAssignmentsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxUrl}/${payload.courseId}/lessons/${payload.lessonId}/assignments/mine`,
            httpRequest.GET,
            null,
            2,
        );
        const data = res?.data ?? res;
        yield put(fetchMySandboxLessonAssignmentsSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchMySandboxLessonAssignmentsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* reviewAssignmentSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxUrl}/${payload.courseId}/assignments/${payload.submissionId}/review`,
            httpRequest.PATCH,
            payload.body,
            0,
        );
        yield put(reviewSandboxAssignmentSuccess(res.data));
        settle(resolve, reject, res.data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(reviewSandboxAssignmentFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchClassAssetsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxUrl}/${payload}/assets`, httpRequest.GET, null, 2);
        const data = res?.data ?? res;
        yield put(fetchSandboxClassAssetsSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchSandboxClassAssetsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* addClassAssetSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxUrl}/${payload.courseId}/assets`, httpRequest.POST, payload.body, 0);
        yield put(addSandboxClassAssetSuccess(res?.data ?? res));
        settle(resolve, reject, res?.data ?? res);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(addSandboxClassAssetFail(error));
        settle(resolve, reject, error, true);
    }
}

function* updateClassAssetSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxUrl}/${payload.courseId}/assets/${payload.assetId}`,
            httpRequest.PATCH,
            payload.body,
            0,
        );
        yield put(updateSandboxClassAssetSuccess(res?.data ?? res));
        settle(resolve, reject, res?.data ?? res);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateSandboxClassAssetFail(error));
        settle(resolve, reject, error, true);
    }
}

function* deleteClassAssetSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        yield call(api, `${sandboxUrl}/${payload.courseId}/assets/${payload.assetId}`, httpRequest.DELETE, null, 0);
        yield put(deleteSandboxClassAssetSuccess(payload.assetId));
        settle(resolve, reject, payload.assetId);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteSandboxClassAssetFail(error));
        settle(resolve, reject, error, true);
    }
}

function* downloadClassAssetSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxUrl}/${payload.courseId}/assets/${payload.assetId}/download`,
            httpRequest.GET,
            null,
            0,
        );
        const data = res?.data ?? res;
        yield put(downloadSandboxClassAssetSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(downloadSandboxClassAssetFail(error));
        settle(resolve, reject, error, true);
    }
}

function* upsertRatingSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxUrl}/${payload.courseId}/ratings`, httpRequest.POST, payload.body, 0);
        const data = res?.data ?? res;
        yield put(upsertSandboxCourseRatingSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(upsertSandboxCourseRatingFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchPaymentHistorySaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const page = payload?.page ?? 1;
    const limit = payload?.limit ?? 40;
    try {
        const res = yield call(
            api,
            `${sandboxAccountPaymentHistoryUrl}?page=${page}&limit=${limit}`,
            httpRequest.GET,
            null,
            0,
        );
        const data = res?.data ?? res;
        yield put(fetchSandboxPaymentHistorySuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchSandboxPaymentHistoryFail(error));
        settle(resolve, reject, error, true);
    }
}

function* uploadCoverImageSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { file: File; courseId: string };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const { file, courseId } = payload;
        const extension = getFileExtension(file) || 'jpeg';
        const response = yield call(api, `${genericUploaderUrl}/${courseId}?extension=${extension}`, httpRequest.POST, {
            collection: 'sandboxCourse',
            directory: 'media',
            path: 'images',
        });
        const data = response?.data?.data ?? response?.data ?? {};
        const uploadUrl = data.url as string | undefined;
        const videoKey = data.videoKey as string | undefined;
        if (!uploadUrl || !videoKey) {
            throw new Error('Cover upload failed — no signed URL returned');
        }
        yield call([axios, axios.put], uploadUrl, file, {
            headers: file.type ? { 'Content-Type': file.type } : undefined,
            maxContentLength: Infinity,
            withCredentials: false,
            'axios-retry': { retries: 2 },
        });
        yield put(uploadSandboxCoverImageSuccess(videoKey));
        settle(resolve, reject, videoKey);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(uploadSandboxCoverImageFail(error));
        settle(resolve, reject, error, true);
    }
}

function* uploadClassAssetZipSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { file: File; courseId: string };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const { file, courseId } = payload;
        if (!isSandboxClassAssetZip(file)) {
            throw new Error('Only ZIP files are supported');
        }
        if (file.size > SANDBOX_CLASS_ASSET_MAX_BYTES) {
            throw new Error('ZIP must be 20MB or smaller');
        }
        const response = yield call(api, `${genericUploaderUrl}/${courseId}?extension=zip`, httpRequest.POST, {
            collection: 'sandboxCourse',
            directory: 'assets',
            path: 'files',
            fileSize: file.size,
        });
        const data = response?.data?.data ?? response?.data ?? {};
        const uploadUrl = data.url as string | undefined;
        const videoKey = data.videoKey as string | undefined;
        if (!uploadUrl || !videoKey) {
            throw new Error('Asset upload failed — no signed URL returned');
        }
        yield call([axios, axios.put], uploadUrl, file, {
            headers: { 'Content-Type': 'application/zip' },
            maxContentLength: Infinity,
            withCredentials: false,
            'axios-retry': { retries: 2 },
        });
        yield put(uploadSandboxClassAssetZipSuccess(videoKey));
        settle(resolve, reject, videoKey);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(uploadSandboxClassAssetZipFail(error));
        settle(resolve, reject, error, true);
    }
}

export default function* sandboxCourseSaga() {
    yield all([
        takeEvery(fetchSandboxExplore as any, fetchExploreSaga),
        takeLatest(fetchSandboxCourse as any, fetchCourseSaga),
        takeLatest(fetchTeacherDashboard as any, fetchTeacherDashboardSaga),
        takeLatest(createSandboxCourse as any, createCourseSaga),
        takeLatest(updateSandboxCourse as any, updateCourseSaga),
        takeLatest(publishSandboxCourse as any, publishCourseSaga),
        takeLatest(enrollSandboxFree as any, enrollFreeSaga),
        takeLatest(completeSandboxPurchase as any, completePurchaseSaga),
        takeLatest(saveSandboxProgress as any, saveProgressSaga),
        takeLatest(fetchLessonProject as any, fetchLessonProjectSaga),
        takeLatest(fetchMySandboxEnrollments as any, fetchEnrollmentsSaga),
        takeLatest(fetchMySandboxAssignments as any, fetchAssignmentsSaga),
        takeLatest(fetchTeacherPendingReviews as any, fetchTeacherReviewsSaga),
        takeLatest(submitSandboxAssignment as any, submitAssignmentSaga),
        takeLatest(fetchMySandboxLessonAssignments as any, fetchLessonAssignmentsSaga),
        takeLatest(reviewSandboxAssignment as any, reviewAssignmentSaga),
        takeLatest(fetchSandboxClassAssets as any, fetchClassAssetsSaga),
        takeLatest(addSandboxClassAsset as any, addClassAssetSaga),
        takeLatest(updateSandboxClassAsset as any, updateClassAssetSaga),
        takeLatest(deleteSandboxClassAsset as any, deleteClassAssetSaga),
        takeLatest(downloadSandboxClassAsset as any, downloadClassAssetSaga),
        takeLatest(upsertSandboxCourseRating as any, upsertRatingSaga),
        takeLatest(fetchSandboxPaymentHistory as any, fetchPaymentHistorySaga),
        takeLatest(uploadSandboxCoverImage as any, uploadCoverImageSaga),
        takeLatest(uploadSandboxClassAssetZip as any, uploadClassAssetZipSaga),
    ]);
}
